import React, { useEffect, useState } from 'react';
import { gql, useApolloClient, useLazyQuery, useQuery } from '@apollo/client';

import { uuid } from 'uuidv4';

import {
    Button,
    DropdownInput,
    IconMusicNotes,
    LoadingEllipses,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { getWebpackEnvVar, removeNulls } from '@trqk/utils';

import {
    useDetectionTracks,
    useTasks,
    useToast,
    useTokenModal,
} from '../../../hooks';
import { TrackMatchingRow } from './components';
import { updateTrqkAudioWithSongId } from './helpers';

const rowsAboveTasks = 3;
const rowsBelowTasks = 1;
const fileAndTrackColWidth = 340;
const gridTemplateColumns = `10px 45px ${fileAndTrackColWidth}px 65px ${fileAndTrackColWidth}px 45px 10px`;

const { GET_SESSION_CURRENT_USER } = queryStrings;

const GET_TRQK_SONG_NAME_MATCHES_FOR_USER = gql`
    query getTrqkSongNameMatchesForUser(
        $id: ID!
        $entity_id: ID
        $file_names: [String!]
    ) {
        getTrqkSongNameMatchesForUser(
            id: $id
            entity_id: $entity_id
            file_names: $file_names
        ) {
            file_name
            songs {
                id
                title
            }
        }
    }
`;

const noMatch = {
    id: null,
    label: '(No match)',
    value: null,
} as DropdownInputOption;

export const MatchTracks = ({
    remove,
    input,
}: ModalContentProps<MatchTracksInput>): JSX.Element => {
    const client = useApolloClient();
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const { getToken } = useTokenModal();
    const { sendTaskEvent } = useTasks();
    const { add: addToast } = useToast();
    const { data: currentUserData } = useQuery(GET_SESSION_CURRENT_USER);
    const { addSongDataToDetectionTracks } = useDetectionTracks();

    const [disabledOptionIds, setDisabledOptionIds] = useState<
        Set<Maybe<string>>
    >(new Set());
    const userId = currentUserData?.session?.currentUser?.id;
    // trqkSongUpdates are the API calls that will be made in order to add an audio file to a
    // trqkSong from an existing statement. They will each be used to trigger a task within
    // handleSubmit() below when the user clicks "Done".
    const [trqkSongUpdates, setTrqkSongUpdates] = useState<TrqkSongUpdate[]>(
        [],
    );
    const [isLoading, setIsLoading] = useState(false);

    // audiosToMatch is the array of TrqkAudioToMatch objects that should each be matched to an existing
    // trqkSong. When MatchTracks is launched from the Tracks footer, these correspond to upload
    // tasks that have been completed by the time this modal mounts, after the user clicked "Add
    // Other Tracks" and uploaded files
    const audiosToMatch = input?.audiosToMatch ?? [];
    const onComplete = input?.onComplete;
    const gridRows = rowsAboveTasks + audiosToMatch.length + rowsBelowTasks;

    const [filenameLists, setFilenameLists] = useState<
        Record<string, DropdownInputOption[]>
    >({});

    const [getNameMatches] = useLazyQuery(GET_TRQK_SONG_NAME_MATCHES_FOR_USER, {
        onCompleted: data => {
            const nameMatchData = data?.getTrqkSongNameMatchesForUser ?? null;

            if (nameMatchData) {
                const nextFilenameLists: Record<string, DropdownInputOption[]> =
                    {};

                nameMatchData.forEach(
                    ({
                        file_name: filename,
                        songs,
                    }: TrqkNameMatchSuggestion): void => {
                        nextFilenameLists[filename] =
                            removeNulls<TrqkNameMatchSongReference>(songs).map(
                                ({
                                    id,
                                    title,
                                }: TrqkNameMatchSongReference): DropdownInputOption => ({
                                    id,
                                    label: title,
                                    value: {
                                        id,
                                        title,
                                    },
                                }),
                            );
                    },
                );

                setFilenameLists(nextFilenameLists);

                const defaultTrqkSongUpdates = audiosToMatch.reduce(
                    (
                        acc: TrqkSongUpdateMap,
                        { trqkAudioId, filename }: TrqkAudioToMatch,
                    ): TrqkSongUpdateMap => {
                        const trqkSongId =
                            nextFilenameLists?.[filename]?.[0]?.value?.id ??
                            null;
                        const title =
                            nextFilenameLists?.[filename]?.[0]?.value?.title ??
                            null;

                        if (
                            !acc.usedFilenames.includes(filename) &&
                            typeof trqkSongId === 'string' &&
                            typeof title === 'string'
                        ) {
                            acc.updates.push({
                                trqkSongId,
                                trqkAudioId,
                                title,
                            });
                            acc.usedFilenames.push(filename);
                        }

                        return acc;
                    },
                    { updates: [], usedFilenames: [] },
                ).updates;

                setTrqkSongUpdates(defaultTrqkSongUpdates);

                setDisabledOptionIds(
                    new Set(
                        defaultTrqkSongUpdates.map(
                            (update: TrqkSongUpdate): string =>
                                update.trqkSongId,
                        ),
                    ),
                );
            }
        },
    });

    useEffect(() => {
        if (userId && audiosToMatch.length > 0) {
            const fileNames = audiosToMatch.map(
                ({ filename }: TrqkAudioToMatch): string => filename,
            );

            getNameMatches({
                variables: {
                    id: userId,
                    file_names: fileNames,
                },
            });
        }
    }, [userId, audiosToMatch, getNameMatches]);

    const handleDropdownInputChange = ({
        trqkAudioId,
        option,
        prevOption,
    }: MatchTracksDropdownInputChange): void => {
        // When updating the list of trqkSong API requests to be made, only add one update per
        // trqkSongId.
        let nextTrqkSongUpdates = [...trqkSongUpdates];

        let foundExistingUpdate = false;

        for (const update of nextTrqkSongUpdates) {
            if (update.trqkSongId === option?.id) {
                // If an existing update is found, update it in place
                foundExistingUpdate = true;

                const newTrqkAudioId = option?.value?.trqkAudioId ?? null;

                /* eslint-disable no-param-reassign */
                if (typeof newTrqkAudioId === 'string') {
                    update.trqkAudioId = newTrqkAudioId;
                }
                /* eslint-enable no-param-reassign */

                break;
            }
        }

        if (!foundExistingUpdate && typeof option?.id === 'string') {
            nextTrqkSongUpdates.push({
                trqkSongId: option?.id,
                trqkAudioId,
                title: option?.label ?? 'unknown track',
            });
        }

        // If the previously selected option is no longer used, remove it from the list of updates
        nextTrqkSongUpdates = nextTrqkSongUpdates.filter(
            (currentUpdate: TrqkSongUpdate): boolean =>
                currentUpdate.trqkSongId !== prevOption?.id,
        );

        setTrqkSongUpdates(nextTrqkSongUpdates);

        setDisabledOptionIds(
            new Set(
                nextTrqkSongUpdates.map(
                    (update: TrqkSongUpdate): string => update.trqkSongId,
                ),
            ),
        );
    };

    const handleSubmit = (): void => {
        const taskCreators = [] as TaskCreator<Record<string, unknown>>[];

        // Update detectionTracks with trqkSong details
        addSongDataToDetectionTracks({
            items: trqkSongUpdates.map(
                ({
                    trqkSongId,
                    trqkAudioId,
                }: TrqkSongUpdate): TrqkSongIdItem => ({
                    trqkSongId,
                    trqkAudioId,
                }),
            ),
            predicate: (
                item: TrqkSongIdItem,
                detectionTrack: DetectionTrack,
            ): boolean => item.trqkAudioId === detectionTrack.audio_id,
        });

        // Add `song_id`s to `trqkAudio`s
        for (const trqkSongUpdate of trqkSongUpdates) {
            const { trqkAudioId, trqkSongId } = trqkSongUpdate;
            const taskId = uuid();

            taskCreators.push({
                taskId,
                onTaskStart: (): void => {
                    updateTrqkAudioWithSongId({
                        client,
                        getToken,
                        sendTaskEvent,
                        taskId,
                        trqkAudioId,
                        trqkDirectoryUrl,
                        trqkSongId,
                    });
                },
            });
        }

        if (taskCreators.length > 0) {
            sendTaskEvent({
                type: 'CREATE_GROUP',
                value: {
                    groupId: uuid(),
                    taskCreators,
                    onTaskUpdate: (task: UnknownTask): void => {
                        if (task.error && !task.canceled) {
                            addToast({
                                content: task.error,
                                kind: 'warning-banner',
                            });
                        }
                    },
                    onGroupStart: (): void => {
                        setIsLoading(true);
                    },
                    onGroupStopped: (
                        onGroupStoppedInput: OnGroupStoppedInput<
                            Record<string, unknown>,
                            { trqkAudioId: string; trqkSongId: string }
                        >,
                    ): void => {
                        setIsLoading(false);

                        if (onComplete) {
                            onComplete(onGroupStoppedInput);
                        }

                        remove({
                            success: true,
                            canceled: false,
                        });
                    },
                },
            });
        } else {
            remove({
                success: true,
                canceled: true,
            });
        }
    };

    const disableDone = trqkSongUpdates.length < 1 || isLoading;

    return (
        <div
            className="bg-white rounded-lg grid"
            style={{
                gridTemplateColumns,
                gridTemplateRows: `repeat(${gridRows} minmax(0, 1fr))`,
            }}
        >
            <div
                className="pt-6 border-l-4 border-primary-500 rounded-tl-lg"
                style={{
                    gridArea: '1 / 1 / 2 / 2',
                    height: 70,
                }}
            />
            <div
                className="pt-6 border-l-4 border-primary-500"
                style={{
                    gridArea: '2 / 1 / 3 / 2',
                }}
            />
            <div
                style={{
                    gridArea: '2 / 3 / 3 / 6',
                }}
            >
                <div className="flex-auto pt-1">
                    <h3>Match Audio Files With Statement Tracks</h3>
                    <div className="text-sm mt-4 mb-12">
                        If your audio file matches a track in your statement,
                        please make sure it is paired correctly.
                    </div>
                </div>
            </div>
            <div
                className="pt-6 border-l-4 border-primary-500"
                style={{
                    gridArea: '3 / 1 / 4 / 2',
                }}
            />
            <div
                style={{
                    gridArea: '3 / 1 / 4 / 3',
                }}
                className="border-b-2 border-highlight-palelavender"
            />
            <div
                style={{
                    gridArea: '3 / 3 / 4 / 4',
                }}
                className="text-sm font-semibold border-b-2 border-highlight-palelavender"
            >
                Audio Files
            </div>
            <div
                style={{
                    gridArea: '3 / 4 / 4 / 5',
                }}
                className="border-b-2 border-highlight-palelavender"
            />
            <div
                style={{
                    gridArea: '3 / 5 / 4 / 6',
                }}
                className="text-sm font-semibold border-b-2 border-highlight-palelavender"
            >
                Tracks from Statements
            </div>
            <div
                style={{
                    gridArea: '3 / 6 / 4 / 8',
                }}
                className="border-b-2 border-highlight-palelavender"
            />
            {audiosToMatch.map(
                (
                    audioToMatch: TrqkAudioToMatch,
                    index: number,
                ): JSX.Element => {
                    const { filename, trqkAudioId } = audioToMatch;
                    let options;
                    let optionLists;

                    if (filenameLists[filename]) {
                        optionLists = [[noMatch], [...filenameLists[filename]]];
                    } else {
                        options = [noMatch];
                    }

                    const defaultId =
                        filenameLists?.[filename]?.[0]?.id ?? null;

                    return (
                        <TrackMatchingRow
                            key={trqkAudioId}
                            filename={filename}
                            gridTemplateColumns={gridTemplateColumns}
                            index={index}
                            rowsAboveTasks={rowsAboveTasks}
                        >
                            <DropdownInput
                                defaultId={defaultId}
                                elementId={trqkAudioId}
                                handleChange={(
                                    prevOption: DropdownInputOption | null,
                                    option: DropdownInputOption | null,
                                ): void => {
                                    handleDropdownInputChange({
                                        prevOption,
                                        option,
                                        filename,
                                        trqkAudioId,
                                    });
                                }}
                                disabledOptionIds={disabledOptionIds}
                                options={options}
                                optionLists={optionLists}
                                resetTimestamp={defaultId === null ? 0 : 1}
                                StatusIcon={IconMusicNotes}
                                width={fileAndTrackColWidth}
                            />
                        </TrackMatchingRow>
                    );
                },
            )}
            <div
                style={{
                    gridArea: `${gridRows} / 5 / ${gridRows + 1} / 6`,
                }}
                className="flex justify-end mt-6 mb-4"
            >
                <div className="w-full h-16 flex items-center">
                    <button
                        type="button"
                        className="flex-none w-1/2 text-primary-500 text-sm font-semibold underline mr-12"
                        onClick={(): void => {
                            remove({
                                success: true,
                                canceled: true,
                            });
                        }}
                    >
                        Skip Matching
                    </button>
                    <div className="flex-grow">
                        <Button
                            color="primary"
                            addClasses="h-10 flex justify-center items-center text-sm"
                            boxShadow={!disableDone}
                            handleClick={handleSubmit}
                            disabled={disableDone}
                        >
                            {!isLoading && <>Done</>}
                            {isLoading && <LoadingEllipses width={40} />}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
